import { useEffect, useRef, useState } from 'react'
import UploadIcon from "@mui/icons-material/Upload";
import { useAppDispatch } from '../../../hooks/StoreHooks';
import { theme } from '../../../theme';
import * as XLSX from "xlsx";
import { setExcelUploadEmployeesParsedData } from '../../../store/reducers/employeesReducer';
import { fetchAllEmployeesThunk, uploadEmployeesThunk } from '../../../store/thunk/employeesThunk';
import { ApplyDocBtn } from '../../actionModules/ActionsBlock.styles';
import { useTranslation } from 'react-i18next';
import { IconButton, Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import { useCompanyEmployees } from '../../../store/selectors/employeesSelectors';
import { StatusTypes } from '../../../types/common';
import { handleRequest } from '../../../store/utils/handleRequest';
import { CallResult, ErrorType } from '../../../api/Requests';

export interface FileUploadData {
    fileContent: string | undefined
}

const firstNameColumn = "Vorname";
const lastNameColumn = "Nachname";
const birthDateColumn = "Geburtsdatum";
const employmentDateColumn = "Anstellungsdatum";
const positionColumn = "Position";
const groupColumn = "Gruppe";
const subCompanyColumn = "Tochterunternehmen";
const employeeNoColumn = "Personalnummer";
const passwordColumn = "Passwort";

const REQUIRED_COLUMNS = [
    firstNameColumn,
    lastNameColumn,
    employeeNoColumn,
];

const FileUploaderBox = () => {
    const dispatch = useAppDispatch();
    const [file, setFile] = useState<File | null>(null);
    const [fileBuffer, setFileBuffer] = useState<ArrayBuffer | null>(null);
    const hiddenFileInput = useRef<HTMLInputElement>(null);
    const [isValid, setIsValid] = useState(true);
    const { t } = useTranslation();

    const handleClick = () => hiddenFileInput.current?.click();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile && selectedFile.name.match(/^.*\.(xls|xlsx|csv)$/)) {
            setFile(selectedFile);
            const reader = new FileReader();
            reader.readAsArrayBuffer(selectedFile);
            reader.onload = (event) => {
                const result = event.target?.result;
                if (result instanceof ArrayBuffer) setFileBuffer(result);
            };
            setIsValid(true);
        }
    };

    const handleOnApplyDoc = async () => {
        if (!fileBuffer) {
            await handleRequest(
                dispatch,
                "direct",
                async () => new CallResult(null, null, ErrorType.UnknownError, t("employeesPage.errors.cantRead")),
                undefined
            );
            setIsValid(false);
            return;
        }

        const workbook = XLSX.read(fileBuffer, { type: "buffer", cellText: false, cellDates: true });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json<Record<string, any>>(worksheet, { blankrows: false, dateNF: 'dd"."mm"."yyyy' });

        if (jsonData.length === 0) {
            await handleRequest(
                dispatch,
                "direct",
                async () => new CallResult(null, null, ErrorType.UnknownError, t("employeesPage.errors.noData")),
                undefined
            );
            setFile(null);
            setIsValid(false);
            return;
        }

        const headers = Object.keys(jsonData[0]).map(header => header?.toString().trim());
        const missingColumns = REQUIRED_COLUMNS.filter(col => !headers.includes(col));
        if (missingColumns.length > 0) {
            await handleRequest(
                dispatch,
                "direct",
                async () => new CallResult(null, null, ErrorType.UnknownError, t("employeesPage.errors.missingColumns")),
                undefined
            );
            setFile(null);
            setIsValid(false);
            return;
        }

        const employees = jsonData.map((row) => {
            const firstName = String(row[firstNameColumn]).trim()
            const lastName = String(row[lastNameColumn]).trim()
            const employeeNumber = String(row[employeeNoColumn]).trim()
            const password = row[passwordColumn] ? String(row[passwordColumn]).trim() : undefined

            const subCompanyId = null;

            const position = row[positionColumn]
                ? String(row[positionColumn]).trim()
                : "";

            const subCompanyName = row[subCompanyColumn]
                ? String(row[subCompanyColumn]).trim()
                : null;

            const groupNames = row[groupColumn]
                ? String(row[groupColumn]).split(",").map(group => group.trim())
                : [];

            const birthDate = row[birthDateColumn]
                ? dayjs(row[birthDateColumn]).format("YYYY-MM-DD")
                : null;

            const employmentDate = row[employmentDateColumn]
                ? dayjs(row[employmentDateColumn]).format("YYYY-MM-DD")
                : null;

            return ({
                firstName,
                lastName,
                birthDate,
                employmentDate,
                position,
                employeeNumber,
                groupNames,
                tachographNumber: "",
                subCompanyName,
                subCompanyId,
                status: StatusTypes.Active,
                password,
            });
        });

        let duplicatesWithinFile: string[] = [];
        let duplicatesWithExistingData: string[] = [];

        //check duplications inside of the file
        const employeeNos = new Set();
        for (const entry of employees) {
            const employeeNo = entry.employeeNumber;
            if (!employeeNo) {
                await handleRequest(
                    dispatch,
                    "direct",
                    async () => new CallResult(null, null, ErrorType.UnknownError, t("employeesPage.errors.duplicateEmployeeNo")),
                    undefined
                );

                dispatch(setExcelUploadEmployeesParsedData([]));
                setFile(null);
                setIsValid(false);
                return;
            }
            if (employeeNos.has(employeeNo)) {
                duplicatesWithinFile.push(employeeNo);
            } else {
                employeeNos.add(employeeNo);
            }
        }
        duplicatesWithinFile = Array.from(new Set(duplicatesWithinFile));
        duplicatesWithExistingData = Array.from(new Set(duplicatesWithExistingData));
        if (duplicatesWithinFile.length > 0 || duplicatesWithExistingData.length > 0) {
            let errorMessage = "";
            if (duplicatesWithinFile.length > 0) {
                errorMessage += t("employeesPage.errors.duplicateEmployeeNo", { employeeNo: duplicatesWithinFile.join(", ") });
            }
            if (duplicatesWithExistingData.length > 0) {
                if (errorMessage) {
                    errorMessage += "\n";
                }
                errorMessage += t("employeesPage.errors.duplicateEmployeeNoAlreadyExists", { employeeNo: duplicatesWithExistingData.join(", ") });
            }

            await handleRequest(
                dispatch,
                "direct",
                async () => new CallResult(null, null, ErrorType.UnknownError, errorMessage),
                undefined
            );

            dispatch(setExcelUploadEmployeesParsedData([]));
            setFile(null);
            setIsValid(false);
            return;
        }

        // Dispatch employees to state and upload
        dispatch(setExcelUploadEmployeesParsedData(employees));
        await dispatch(uploadEmployeesThunk());
        setFile(null);
        setIsValid(true);
    };

    return (
        <div>
            {file && isValid ? (
                <ApplyDocBtn sx={{ width: "100%", height: "100%", fontSize: "60%" }} onClick={handleOnApplyDoc}>
                    {t("buttons.upload")}
                </ApplyDocBtn>
            ) : (
                <Tooltip title={t("employeesPage.uploadEmployeeList")}>
                    <IconButton onClick={handleClick}>
                        <input type="file" ref={hiddenFileInput} style={{ display: "none" }} onChange={handleChange} />
                        <UploadIcon sx={{ color: file ? theme.palette.border.subform : theme.palette.companyMain.main }} />
                    </IconButton>
                </Tooltip>
            )}
        </div>
    );
};

export default FileUploaderBox;
